import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Search, Car, Map as MapIcon } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { RideCard } from "@/components/RideCard";
import { ThiriMascot } from "@/components/ThiriMascot";
import { MapsProvider, LocationField, RidesPinMap, RouteMap } from "@/components/maps";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const VEHICLES = ["car", "bike", "auto", "cab"];
const today = () => new Date().toISOString().slice(0, 10);

export default function Rides() {
  const { user, refreshUser } = useAuth();
  const [params] = useSearchParams();
  const location = useLocation();
  const initialTab = params.get("tab") || (location.pathname.endsWith("/offer") ? "offer" : location.pathname.endsWith("/my") ? "my" : "find");
  const [tab, setTab] = useState(initialTab);

  const [search, setSearch] = useState({ pickup: { text: "", lat: null, lng: null }, destination: { text: "", lat: null, lng: null }, date: today(), time: "", vehicle_type: "", max_price: "", only_buddies: false, only_verified: false });
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const [offer, setOffer] = useState({ from: { text: "", lat: null, lng: null }, to: { text: "", lat: null, lng: null }, date: today(), departure_time: "08:00", vehicle_type: "car", available_seats: 3, contribution: 0, description: "", pickup_flexibility: "exact", destination_flexibility: "exact" });
  const [offering, setOffering] = useState(false);
  const [mine, setMine] = useState(null);
  const [mapRides, setMapRides] = useState(null);
  const [selectedRide, setSelectedRide] = useState(null);
  const onPin = useCallback((r) => setSelectedRide(r), []);

  useEffect(() => {
    if (tab === "my") api.get("/rides/my").then(({ data }) => setMine(data)).catch(() => {});
    if (tab === "map") api.get("/rides/map").then(({ data }) => setMapRides(data)).catch(() => setMapRides([]));
  }, [tab]);

  const doSearch = async (e) => {
    e?.preventDefault();
    setSearching(true);
    try {
      const payload = {
        pickup: search.pickup.text, destination: search.destination.text,
        pickup_lat: search.pickup.lat, pickup_lng: search.pickup.lng,
        destination_lat: search.destination.lat, destination_lng: search.destination.lng,
        date: search.date, time: search.time || null,
        vehicle_type: search.vehicle_type || null,
        max_price: search.max_price ? Number(search.max_price) : null,
        only_buddies: search.only_buddies, only_verified: search.only_verified,
      };
      const { data } = await api.post("/rides/search", payload);
      setResults(data);
    } catch (err) { toast.error(apiError(err)); } finally { setSearching(false); }
  };

  const doOffer = async (e) => {
    e.preventDefault();
    setOffering(true);
    try {
      await api.post("/rides", {
        from_location: offer.from.text, to_location: offer.to.text,
        from_lat: offer.from.lat, from_lng: offer.from.lng,
        to_lat: offer.to.lat, to_lng: offer.to.lng,
        date: offer.date, departure_time: offer.departure_time, vehicle_type: offer.vehicle_type,
        available_seats: Number(offer.available_seats), contribution: Number(offer.contribution),
        description: offer.description, pickup_flexibility: offer.pickup_flexibility,
        destination_flexibility: offer.destination_flexibility,
      });
      toast.success("Ride offered! Your campus can now find it.");
      setTab("my");
    } catch (err) { toast.error(apiError(err)); } finally { setOffering(false); }
  };

  return (
    <MapsProvider>
    <div className="space-y-6" data-testid="rides-page">
      <h1 className="font-display font-black text-3xl">Rides</h1>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-full">
          <TabsTrigger data-testid="tab-find-ride" value="find" className="rounded-full">Find Ride</TabsTrigger>
          <TabsTrigger data-testid="tab-offer-ride" value="offer" className="rounded-full">Offer Ride</TabsTrigger>
          <TabsTrigger data-testid="tab-my-rides" value="my" className="rounded-full">My Rides</TabsTrigger>
          <TabsTrigger data-testid="tab-rides-map" value="map" className="rounded-full">Map</TabsTrigger>
        </TabsList>

        <TabsContent value="find" className="mt-6 space-y-6">
          <form onSubmit={doSearch} className="t-card p-6 grid sm:grid-cols-2 gap-4">
            {search.pickup.lat != null && search.destination.lat != null && (
              <div className="sm:col-span-2">
                <RouteMap from={{ lat: search.pickup.lat, lng: search.pickup.lng }} to={{ lat: search.destination.lat, lng: search.destination.lng }} />
              </div>
            )}
            <LocationField testId="search-pickup-input" required placeholder="Pickup (e.g. Miyapur)" value={search.pickup}
              onChange={(v) => setSearch((s) => ({ ...s, pickup: v }))} className="rounded-2xl" />
            <LocationField testId="search-destination-input" required placeholder="Destination (e.g. HMRITM campus)" value={search.destination}
              onChange={(v) => setSearch((s) => ({ ...s, destination: v }))} className="rounded-2xl" />
            <Input data-testid="search-date-input" type="date" required value={search.date}
              onChange={(e) => setSearch({ ...search, date: e.target.value })} className="rounded-2xl" />
            <Input data-testid="search-time-input" type="time" value={search.time}
              onChange={(e) => setSearch({ ...search, time: e.target.value })} className="rounded-2xl" />
            <Select value={search.vehicle_type} onValueChange={(v) => setSearch({ ...search, vehicle_type: v === "any" ? "" : v })}>
              <SelectTrigger data-testid="search-vehicle-select" className="rounded-2xl"><SelectValue placeholder="Any vehicle" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any vehicle</SelectItem>
                {VEHICLES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input data-testid="search-maxprice-input" type="number" min="0" placeholder="Max price ₹ (optional)" value={search.max_price}
              onChange={(e) => setSearch({ ...search, max_price: e.target.value })} className="rounded-2xl" />
            <label className="flex items-center gap-2 text-sm font-semibold">
              <Switch data-testid="search-only-buddies-switch" checked={search.only_buddies} onCheckedChange={(v) => setSearch({ ...search, only_buddies: v })} /> Only buddies
            </label>
            <label className="flex items-center gap-2 text-sm font-semibold">
              <Switch data-testid="search-only-verified-switch" checked={search.only_verified} onCheckedChange={(v) => setSearch({ ...search, only_verified: v })} /> Only verified drivers
            </label>
            <Button data-testid="search-submit-btn" disabled={searching} type="submit" className="sm:col-span-2 rounded-full font-bold">
              <Search className="w-4 h-4 mr-2" /> {searching ? "Matching…" : "Find matching rides"}
            </Button>
          </form>

          {results !== null && (
            results.length ? (
              <div className="space-y-4" data-testid="search-results">
                {results.map((r) => <RideCard key={r.id} ride={r} />)}
              </div>
            ) : (
              <div className="space-y-4">
                <ThiriMascot state="thinking" message="No matching rides yet for that route. Try widening the time, or offer the ride yourself — someone's probably looking for it!" />
                <div className="t-card p-8 text-center text-sm text-muted-foreground" data-testid="no-search-results">No rides found for this search.</div>
              </div>
            )
          )}
        </TabsContent>

        <TabsContent value="offer" className="mt-6 space-y-6">
          {(!user?.phone || !user?.vehicle_type) && (
            <DriverProfileGate user={user} onDone={refreshUser} />
          )}
          <form onSubmit={doOffer} className="t-card p-6 grid sm:grid-cols-2 gap-4">
            {offer.from.lat != null && offer.to.lat != null && (
              <div className="sm:col-span-2">
                <RouteMap from={{ lat: offer.from.lat, lng: offer.from.lng }} to={{ lat: offer.to.lat, lng: offer.to.lng }} />
              </div>
            )}
            <LocationField testId="offer-from-input" required placeholder="From" value={offer.from}
              onChange={(v) => setOffer((s) => ({ ...s, from: v }))} className="rounded-2xl" />
            <LocationField testId="offer-to-input" required placeholder="To" value={offer.to}
              onChange={(v) => setOffer((s) => ({ ...s, to: v }))} className="rounded-2xl" />
            <Input data-testid="offer-date-input" type="date" required value={offer.date}
              onChange={(e) => setOffer({ ...offer, date: e.target.value })} className="rounded-2xl" />
            <Input data-testid="offer-time-input" type="time" required value={offer.departure_time}
              onChange={(e) => setOffer({ ...offer, departure_time: e.target.value })} className="rounded-2xl" />
            <Select value={offer.vehicle_type} onValueChange={(v) => setOffer({ ...offer, vehicle_type: v })}>
              <SelectTrigger data-testid="offer-vehicle-select" className="rounded-2xl"><SelectValue /></SelectTrigger>
              <SelectContent>{VEHICLES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="offer-seats-input" type="number" min="1" max="10" required placeholder="Available seats" value={offer.available_seats}
              onChange={(e) => setOffer({ ...offer, available_seats: e.target.value })} className="rounded-2xl" />
            <Input data-testid="offer-contribution-input" type="number" min="0" required placeholder="Contribution per passenger ₹" value={offer.contribution}
              onChange={(e) => setOffer({ ...offer, contribution: e.target.value })} className="rounded-2xl" />
            <Select value={offer.pickup_flexibility} onValueChange={(v) => setOffer({ ...offer, pickup_flexibility: v })}>
              <SelectTrigger data-testid="offer-pickup-flex-select" className="rounded-2xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="exact">Exact pickup point</SelectItem>
                <SelectItem value="nearby">Can pick up nearby</SelectItem>
                <SelectItem value="on_route">Anywhere on route</SelectItem>
              </SelectContent>
            </Select>
            <Textarea data-testid="offer-description-input" placeholder="Description (optional) — landmarks, rules, music taste…" value={offer.description}
              onChange={(e) => setOffer({ ...offer, description: e.target.value })} className="sm:col-span-2 rounded-2xl" />
            <Button data-testid="offer-submit-btn" disabled={offering} type="submit" className="sm:col-span-2 rounded-full font-bold">
              <Car className="w-4 h-4 mr-2" /> {offering ? "Publishing…" : "Offer this ride"}
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="my" className="mt-6 space-y-6">
          <section>
            <h2 className="font-display font-bold text-lg mb-3">Rides I'm offering</h2>
            {mine?.offered?.length ? (
              <div className="space-y-4">
                {mine.offered.map((r) => (
                  <div key={r.id} className="relative">
                    <RideCard ride={r} />
                    {r.pending_requests > 0 && (
                      <span className="absolute top-3 right-3 bg-primary text-white text-xs font-bold rounded-full px-2 py-0.5">
                        {r.pending_requests} request{r.pending_requests !== 1 ? "s" : ""}
                      </span>
                    )}
                    <span className="absolute bottom-3 right-3 t-chip bg-black/5">{r.status}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="t-card p-6 text-center text-sm text-muted-foreground" data-testid="no-offered-rides">You haven't offered any rides yet.</div>
            )}
          </section>
          <section>
            <h2 className="font-display font-bold text-lg mb-3">Rides I've joined</h2>
            {mine?.joined?.length ? (
              <div className="space-y-4">
                {mine.joined.map(({ request, ride }) => (
                  <div key={request.id} className="relative">
                    <RideCard ride={ride} />
                    <span className={`absolute top-3 right-3 t-chip ${request.status === "accepted" ? "bg-secondary/20 text-[#14532D]" : request.status === "requested" ? "bg-[#F9C31F] text-[#12211C]" : "bg-black/5"}`}>
                      {request.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="t-card p-6 text-center text-sm text-muted-foreground" data-testid="no-joined-rides">No ride requests yet. Find a ride to get started.</div>
            )}
          </section>
        </TabsContent>

        <TabsContent value="map" className="mt-6 space-y-4">
          {mapRides === null ? (
            <p className="text-muted-foreground animate-pulse text-center py-10">Loading map…</p>
          ) : mapRides.length ? (
            <>
              <div className="t-card overflow-hidden">
                <RidesPinMap rides={mapRides} onSelect={onPin} />
                <p className="px-5 py-3 text-sm font-bold flex items-center gap-2">
                  <MapIcon className="w-4 h-4 text-primary" /> {mapRides.length} active ride{mapRides.length !== 1 ? "s" : ""} — tap a pin to preview
                </p>
              </div>
              {selectedRide && <RideCard ride={selectedRide} />}
            </>
          ) : (
            <div className="t-card p-10 text-center" data-testid="empty-rides-map">
              <MapIcon className="w-8 h-8 mx-auto text-muted-foreground" />
              <p className="font-display font-bold mt-3">No mapped rides yet</p>
              <p className="text-sm text-muted-foreground mt-1">Rides offered with a picked location show up here as pins.</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
    </MapsProvider>
  );
}

function DriverProfileGate({ user, onDone }) {
  const [dp, setDp] = useState({ phone: user?.phone || "", vehicle_type: user?.vehicle_type || "", vehicle_model: user?.vehicle_model || "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!dp.phone.trim() || !dp.vehicle_type) return toast.error("Phone number and vehicle type are required");
    setSaving(true);
    try {
      await api.put("/users/me", dp);
      await onDone();
      toast.success("Driver profile saved — you're ready to offer rides!");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="t-card p-6 border-2 border-primary/30" data-testid="driver-profile-gate">
      <h2 className="font-display font-bold flex items-center gap-2"><Car className="w-5 h-5 text-primary" /> Set up your driver profile</h2>
      <p className="text-sm text-muted-foreground mt-1">To offer rides, riders need your contact number and vehicle details.</p>
      <div className="grid sm:grid-cols-3 gap-3 mt-4">
        <Input data-testid="driver-phone-input" required placeholder="Phone number" value={dp.phone}
          onChange={(e) => setDp({ ...dp, phone: e.target.value })} className="rounded-2xl" />
        <Select value={dp.vehicle_type} onValueChange={(v) => setDp({ ...dp, vehicle_type: v })}>
          <SelectTrigger data-testid="driver-vehicle-select" className="rounded-2xl"><SelectValue placeholder="Vehicle type" /></SelectTrigger>
          <SelectContent>{VEHICLES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <Input data-testid="driver-model-input" placeholder="Model (e.g. Activa 6G, Swift)" value={dp.vehicle_model}
          onChange={(e) => setDp({ ...dp, vehicle_model: e.target.value })} className="rounded-2xl" />
      </div>
      <Button data-testid="driver-profile-save-btn" disabled={saving} onClick={save} className="mt-4 rounded-full font-bold">
        {saving ? "Saving…" : "Save driver profile"}
      </Button>
    </div>
  );
}
